import { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import HaloPlugin from './custom_plugins/JobtrackBar/HaloPlugin';
import { Row, useIsMobile } from "./Flexbox";
import Text from './Text';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title, ChartDataLabels, HaloPlugin);
const CHART_MIN_WIDTH = 620;
const HIDE_SCROLLBAR = 'jobtrack-bar-no-scrollbar';
const JobtrackBarItem = (props) => {
    const chartRef = useRef(null);
    const isMobile = useIsMobile();
    const fixedWidth = 320;

    const hexToRgba = (hex, alpha = 1) => {
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const chartConfig = {
        labels: [props.barData.label], 
        datasets: [
            {
                id: 'mainBarDataset',
                data: [props.barData.mainValue],
                backgroundColor: [props.barData.color], 
                stack: 'efficiency',
                originalValues: [props.barData.originalValue],
                barThickness: 60,
                datalabels: {display: false} 
            },
            {
                id: 'remainingDataset',
                data:[props.barData.originalValue > 100 ? 0 : (100 - props.barData.mainValue)], //We get the spare space
                backgroundColor: props.theme.Ey,
                stack: 'efficiency',
                originalValues: [props.barData.originalValue],
                barThickness: 60,
                datalabels: props.barData.originalValue < 100 ? { display: true } : { display: false }
            },
            {
                id: 'overflowDataset',
                data: [props.barData.overflowValue],
                backgroundColor: [props.barData.overflowColor], 
                stack: 'efficiency',
                barThickness: 25,   //This value is lower than the mainBarDataset due to the "halo" effect (this would look bigger if it had the same size)
                originalValues: [props.barData.originalValue],
                datalabels: props.barData.originalValue < 100 ? { display: false } : { display: true },
            }
        ],
    };

    const options = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: false,   //If true, it'll show a "delay" animation by default
        layout: {padding: {right: 48}},  //Absolute room for the percentage drawn past the end of the bar, the 5% headroom in scales.x is proportional and shrinks with the plot
        onClick: (e, elements) => {
            if (elements.length > 0) {
                props.onClick(props.barData.index); 
            }
        },
        plugins: {
            legend: { 
                display: false,  //Not needed
            },
            title: { 
                display: false,  //Not needed either
            },
            tooltip: { 
                enabled: false, //Use enabled here, otherwise it'll continue showing up on hover!
            },
            datalabels: {
                display: true, 
                clamp: true,
                align: 'end',   //Modify the position of the percentage (this and anchor must be the same)
                anchor: 'end',
                formatter: function (value, context) {
                    const originalValue = context.dataset.originalValues[0];
                    return `${originalValue}%`;
                },
                color: props.theme.ONED,
                offset: 4
            },
            haloEffect: {
                haloDatasetId: 'overflowDataset',   //Only for the excess
                haloColor: hexToRgba(props.theme.Yellow, 1),
                haloBlur: 3,    //Modify this value until the desired "halo" is reached
                haloOffsetX: 0,
                haloOffsetY: 0,
            }
        },
        scales: {
            x: {
                stacked: true,
                beginAtZero: true,
                min: props.maxGlobalPercentage < 0 ? -Math.abs(props.maxGlobalPercentage) * 1.05 : 0,   //The ".06" adds a small visual buffer
                max: props.maxGlobalPercentage * 1.05,
                grid: { 
                    display: false 
                },
                title: { 
                    display: false 
                },
                ticks: { 
                    display: false 
                },
                border: {
                    display: false
                }
            },
            y: {
                stacked: true,
                title: { 
                    display: false 
                },
                grid: { 
                    display: false 
                },
                ticks: {
                    display: true,
                    crossAlign: 'far',  //Aligns names to the left (Can be near | center | far)
                    font: { 
                        size: 15 
                    },
                    color: props.theme.ONED,
                },
                afterFit: function(scale) { //Reserves a specific space for the ticks, forcing the bars to align
                    scale.width = fixedWidth;
                },
                border: {
                    display: false
                }
            },
        },
    };

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className={isMobile ? HIDE_SCROLLBAR : undefined} style={isMobile ? { width: '100%', overflowX: 'auto' } : undefined}>
                {isMobile && <style>{`.${HIDE_SCROLLBAR}::-webkit-scrollbar { display: none; }`}</style>}
                <div style={{ height: 50, width: '100%', minWidth: isMobile ? CHART_MIN_WIDTH : undefined }}>
                    <Bar data={chartConfig} options={options} ref={chartRef}/>
                </div>
            </div>
            {props.isSelected && props.details && props.details.length > 0 && (
                <div style={{gap: 16}}>
                    {[...props.details].sort((a, b) => b.percentage - a.percentage).map((item) => (
                        <Row key={props.selection.resource === "CodRecurso" ? item.SecondCol : `${item.NumOrdem}-${item.CodRecurso}-${item.Fecha}`} style={{width: '100%', padding: 5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'start'}}>
                        {props.selection.resource === "CodRecurso" ? (
                            <>
                                <Text size={15} color={props.theme.Lao} style={{width: fixedWidth - 5,}}>
                                    {item.SecondCol}
                                </Text>
                                <Text size={15} color={props.theme.Lao} style={{textAlign: 'start', width: 90}}>
                                    {item.percentage}%
                                </Text>
                            </>
                        ) : (
                            <>
                                <Text size={15} color={props.theme.Lao} style={{width: fixedWidth - 5}}>
                                    OP-{item.NumOrdem}
                                </Text>
                                <Text size={15} color={props.theme.Lao} style={{flex: 1, textAlign: 'left', marginRight: 40}}>
                                    {item.percentage}%
                                </Text>
                                <Text size={15} color={props.theme.Lao} style={{width: 125}}>
                                    {item.SecondCol}
                                </Text>
                                <Text size={15} color={props.theme.Lao} style={{textAlign: 'right', width: 90}}>
                                    {item.Fecha}
                                </Text>
                            </>
                        )}
                        </Row>
                    ))}
                </div>
            )}
        </div>
    );
};

export default JobtrackBarItem;